/*
 * A página inicial do chat (/chat sem canal): em vez de abrir sozinha a
 * última conversa, uma lista com mais opções, na moldura do chat do tema.
 *
 * - No computador, as abas no topo (Canais, Mensagens diretas, Meus tópicos,
 *   Pesquisar e, para moderadores, Salvos/Denúncias); no celular, as abas de
 *   baixo (c-footer).
 * - Canais: "Continuar em #canal" (a última conversa) no topo, os botões
 *   "Explorar canais" e "Novo canal" e cada canal com o ícone, o nome, a
 *   descrição curta, a hora da última atividade e o indicador de não lidas.
 * - Mensagens diretas: o avatar, a última mensagem e a hora, e o "Nova
 *   mensagem" (o gancho `newMessage` dos recursos, em ./features).
 * - Meus tópicos, Pesquisar, Salvos e Denúncias são as telas do ramon/chat.
 */
import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import Button from 'flarum/common/components/Button';
import Icon from 'flarum/common/components/Icon';
import classList from 'flarum/common/utils/classList';
import { formatWith } from '../../utils/dates';
import { t } from '../../utils/settings';
import { channelUrl, chatState, openChannel, ramonChannelIcon, ramonModule } from './bridge';
import { safely, sidebarHooks } from './hooks';
import { ChatFooter, Navbar, UnreadIndicator, ct, toDrawer } from './ui';
/** A conversa a continuar: a última aberta antes de chegar à lista. */
let continueId = null;
export function rememberContinue(id) {
  if (id !== null) continueId = id;
}
function rt(key, params = {}) {
  return app.translator.trans(`ramon-chat.forum.${key}`, params, true);
}
function tr(key, params = {}) {
  return t(`thirdparty.ramon_chat.${key}`, params);
}
function phone() {
  return window.innerWidth < 768;
}
function startOfDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

/** "14:35" hoje, "ontem"/"13 out" antes — a hora da última atividade. */
function activity(date) {
  if (!date) return '';
  const days = Math.round((startOfDay(new Date()) - startOfDay(date)) / 86400000);
  if (days === 0) return formatWith(date, {
    hour: '2-digit',
    minute: '2-digit'
  });
  if (days === 1) return ct('chat_message_separator.yesterday');
  return formatWith(date, {
    day: 'numeric',
    month: 'short'
  }).replace(/\./g, '').replace(/\sde\s/g, ' ');
}

/** O texto da última mensagem, sem marcação, com o autor nas MDs em grupo. */
function lastMessageText(channel) {
  const last = safely(() => channel.lastMessage?.(), null);
  if (!last) return '';
  const html = String(safely(() => last.contentHtml?.() || last.content?.() || '', ''));
  const div = document.createElement('div');
  div.innerHTML = html;
  const text = (div.textContent || '').replace(/\s+/g, ' ').trim();
  const author = safely(() => last.user?.()?.displayName?.() || '', '');
  const mine = safely(() => last.user?.() === app.session.user, false);
  if (!text) return '';
  return mine ? `${ct('you')}: ${text}` : channel.isDirect() && (channel.others() || []).length > 1 && author ? `${author}: ${text}` : text;
}
function sorted(channels, section) {
  const sort = sidebarHooks.sortChannels;
  return sort ? safely(() => sort(channels.slice(0), section), channels) : channels;
}
function lastAt(channel) {
  return safely(() => channel.lastMessageAt?.() || null, null);
}

/** Uma linha da lista: ícone, nome (+ selo), texto de apoio, hora e indicador. */
function row(channel, subtitle) {
  const id = Number(channel.id());
  const direct = channel.isDirect();
  const mentions = channel.unreadMentionsCount() ?? 0;
  const unread = channel.hasUnread();
  const urgent = direct ? channel.isMuted() ? 0 : channel.unreadCount() ?? 0 : mentions;
  return m("li", {
    key: id,
    className: "CanelaRamonChatLobby-item"
  }, m("a", {
    className: classList('CanelaRamonChatLobby-row', {
      'has-unread': unread || urgent > 0,
      muted: channel.isMuted()
    }),
    href: channelUrl(id),
    onclick: e => {
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
      e.preventDefault();
      openChannel(id);
    }
  }, m("span", {
    className: classList('CanelaRamonChatLobby-icon', {
      '--avatar': direct
    })
  }, ramonChannelIcon(channel, 'CanelaRamonChatLobby-glyph')), m("span", {
    className: "CanelaRamonChatLobby-main"
  }, m("span", {
    className: "CanelaRamonChatLobby-name"
  }, m("span", {
    className: "CanelaRamonChatLobby-label"
  }, channel.displayName()), channel.isPrivate() && !direct && m(Icon, {
    name: "fas fa-lock",
    className: "CanelaRamonChatLobby-lock"
  }), sidebarHooks.channelSuffix && safely(() => sidebarHooks.channelSuffix(channel), null)), subtitle && m("span", {
    className: "CanelaRamonChatLobby-subtitle"
  }, subtitle)), m("span", {
    className: "CanelaRamonChatLobby-meta"
  }, m("span", {
    className: "CanelaRamonChatLobby-time"
  }, activity(lastAt(channel))), UnreadIndicator(urgent, unread))));
}
export default class ChatLobby extends Component {
  oninit(vnode) {
    super.oninit(vnode);
    const state = chatState();
    if (state && !state.channelsLoaded && !state.channelsLoading) state.loadChannels().then(() => m.redraw(), () => {});
  }
  view() {
    const {
      tab
    } = this.attrs;
    const onPhone = phone();
    const actions = onPhone ? [] : [{
      key: 'drawer',
      icon: 'fas fa-down-left-and-up-right-to-center',
      title: ct('close_full_page'),
      className: 'c-navbar__open-drawer-button',
      onclick: () => toDrawer()
    }];
    return m("div", {
      className: "full-page-chat full-page-chat-sidebar-enabled CanelaRamonChatHome CanelaRamonChatLobby"
    }, m("div", {
      id: "main-chat-outlet",
      className: "main-chat-outlet chat-view"
    }, m("div", {
      className: `c-drawer-routes c-routes --${tab === 'dms' ? 'direct-messages' : 'channels'}`
    }, Navbar({
      title: ct('heading'),
      actions
    }), !onPhone && m(LobbyTabs, {
      current: tab
    }), m("div", {
      className: "chat-drawer-content"
    }, m("div", {
      className: "CanelaRamonChatLobby-body"
    }, tab === 'dms' ? this.directTab() : this.channelsTab())), onPhone && m(ChatFooter, {
      current: tab
    }))));
  }
  channelsTab() {
    const state = chatState();
    const channels = sorted(state?.categoryChannels() || [], 'channels');
    const canCreate = !!app.forum.attribute('canCreateChatChannel');
    const resume = continueId !== null ? state?.channels.find(c => Number(c.id()) === continueId) || null : null;
    return [resume ? this.continueRow(resume) : null, m("div", {
      className: "CanelaRamonChatLobby-actions"
    }, m("h2", {
      className: "CanelaRamonChatLobby-heading"
    }, ct('chat_channels')), m(Button, {
      className: "Button",
      icon: "fas fa-compass",
      onclick: () => m.route.set(app.route('chat.browse'))
    }, rt('sidebar.browse_channels')), canCreate && m(Button, {
      className: "Button Button--primary",
      icon: "fas fa-plus",
      onclick: () => createChannel()
    }, rt('sidebar.new_channel'))), channels.length ? m("ul", {
      className: "CanelaRamonChatLobby-list"
    }, channels.map(channel => row(channel, channel.description() || ''))) : state?.channelsLoaded ? m("p", {
      className: "CanelaRamonChatLobby-empty"
    }, ct('no_public_channels')) : null];
  }
  directTab() {
    const state = chatState();
    const direct = sorted(state?.directChannels() || [], 'direct');
    const create = sidebarHooks.newMessage && app.forum.attribute('canStartChatDirect') ? sidebarHooks.newMessage : null;
    return [m("div", {
      className: "CanelaRamonChatLobby-actions"
    }, m("h2", {
      className: "CanelaRamonChatLobby-heading"
    }, ct('direct_messages.sidebar_title')), create && m(Button, {
      className: "Button Button--primary",
      icon: "fas fa-plus",
      onclick: () => create()
    }, ct('direct_messages.new_group'))), direct.length ? m("ul", {
      className: "CanelaRamonChatLobby-list"
    }, direct.map(channel => row(channel, lastMessageText(channel)))) : state?.channelsLoaded ? m("p", {
      className: "CanelaRamonChatLobby-empty"
    }, ct('no_direct_message_channels')) : null];
  }

  /** "Continuar em #canal": a última conversa, no topo, sem abrir sozinha. */
  continueRow(channel) {
    const id = Number(channel.id());
    return m("a", {
      className: "CanelaRamonChatLobby-continue",
      href: channelUrl(id),
      onclick: e => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
        e.preventDefault();
        openChannel(id);
      }
    }, m("span", {
      className: "CanelaRamonChatLobby-continueLabel"
    }, tr('continue_in')), m("span", {
      className: "CanelaRamonChatLobby-icon"
    }, ramonChannelIcon(channel, 'CanelaRamonChatLobby-glyph')), m("span", {
      className: "CanelaRamonChatLobby-label"
    }, channel.displayName()), m(Icon, {
      name: "fas fa-arrow-right",
      className: "CanelaRamonChatLobby-continueArrow"
    }));
  }
}

/** Abre o formulário de canal do ramon/chat (o mesmo do "+" da lista dele). */
function createChannel() {
  const lazy = ramonModule('forum/utils/lazy');
  if (!lazy?.loadChannelFormModal) {
    m.route.set(app.route('chat.browse'));
    return;
  }
  app.modal.show(lazy.loadChannelFormModal, {
    onSaved: channel => {
      chatState()?.setActiveChannel(Number(channel.id()));
      m.route.set(app.route('chat.channel', {
        id: channel.id()
      }));
    }
  });
}

/** As abas do topo, no computador (Salvos e Denúncias só para quem modera). */
export class LobbyTabs extends Component {
  view() {
    const current = this.attrs.current;
    const tabs = [['channels', 'fas fa-comments', ct('channel_list.title'), `${app.route('chat.index')}?tab=channels`], ['dms', 'fas fa-users', ct('direct_messages.sidebar_title'), `${app.route('chat.index')}?tab=dms`], ['threads', 'fas fa-list-ul', ct('my_threads.title'), app.route('chat.threads')], ['search', 'fas fa-magnifying-glass', ct('search.short_title'), app.route('chat.search')]];
    if (app.forum.attribute('canModerateChat')) {
      tabs.push(['bookmarks', 'far fa-bookmark', rt('sidebar.bookmarks'), app.route('chat.bookmarks')]);
      tabs.push(['flags', 'far fa-flag', rt('sidebar.flags'), app.route('chat.flags')]);
    }
    return m("nav", {
      className: "CanelaRamonChatLobby-tabs"
    }, m("ul", null, tabs.map(([name, icon, label, href]) => m("li", {
      key: name
    }, m("a", {
      className: classList('CanelaRamonChatLobby-tab', {
        active: current === name
      }),
      href: href,
      onclick: e => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
        e.preventDefault();
        m.route.set(href);
      }
    }, m(Icon, {
      name: icon
    }), m("span", null, label))))));
  }
}
flarum.reg.add('ramon-canela', 'forum/thirdparty/ramonChat/lobby', ChatLobby);